<template>
    <div :class="$store.state.routeLoaded ? 'slide-up view' : 'hide'">
        <div class="us">
            <router-link to="/ai-hub?tab=health" class="us-back">&larr; Back to The Forge</router-link>
            <div v-if="denied" class="us-empty">This page is only available to IronBook administrators.</div>
            <div v-else-if="error" class="us-empty" role="alert">{{ error }} <button type="button" class="us-retry" @click="load()">Try again</button></div>
            <div v-else-if="!data" class="us-empty" role="status">Loading IronBook usage…</div>
            <template v-else>
                <div class="us-kicker">IronBook usage &middot; every company &middot; as of {{ generated }}</div>
                <h2 class="us-title">Who is using IronBook</h2>
                <p class="us-lede">Counts per company. "Active" means a team login opened IronBook in that window. Activity columns cover the last 30 days.</p>

                <section class="us-strip" aria-label="Last 30 days, all companies">
                    <div v-for="s in strip" :key="s.label" class="us-stat">
                        <div class="us-stat-n">{{ s.value }}</div>
                        <div class="us-stat-l">{{ s.label }}</div>
                        <div v-if="s.sub" class="us-stat-s">{{ s.sub }}</div>
                    </div>
                </section>

                <div v-if="data.unavailable?.length" class="us-warn" role="note">Some numbers could not be read and show as 0: {{ data.unavailable.join(', ') }}.</div>

                <h3 class="us-h">By company</h3>
                <p class="us-hint">Click a column heading to sort.</p>
                <div class="us-tablewrap">
                    <table class="us-table">
                        <thead>
                            <tr>
                                <th v-for="c in columns" :key="c.key" scope="col" :class="[c.key == 'name' ? 'us-name' : 'us-num', sortKey == c.key ? 'us-sorted' : '']" :aria-sort="sortKey == c.key ? (sortDir > 0 ? 'ascending' : 'descending') : 'none'">
                                    <button type="button" class="us-sort" :title="c.title" @click="sortBy(c.key)">{{ c.label }}<span v-if="sortKey == c.key" aria-hidden="true">{{ sortDir > 0 ? ' ▲' : ' ▼' }}</span></button>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in sorted" :key="row.id" :class="row.id == ownCompany ? 'us-own' : ''">
                                <td v-for="c in columns" :key="c.key" :class="c.key == 'name' ? 'us-name' : 'us-num'">
                                    <template v-if="c.key == 'name'">{{ row.name }} <span class="us-id">#{{ row.id }}</span></template>
                                    <template v-else>{{ fmt(c, c.get(row)) }}</template>
                                </td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <td v-for="c in columns" :key="c.key" :class="c.key == 'name' ? 'us-name' : 'us-num'">
                                    <template v-if="c.key == 'name'">All companies</template>
                                    <template v-else>{{ fmt(c, c.get(data.totals)) }}</template>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                <h3 class="us-h">Published content by type</h3>
                <div class="us-tablewrap">
                    <table class="us-table">
                        <thead>
                            <tr>
                                <th scope="col" class="us-name">Company</th>
                                <th v-for="t in types" :key="t" scope="col" class="us-num">{{ typeLabel(t) }}</th>
                                <th scope="col" class="us-num">Folders</th>
                                <th scope="col" class="us-num" title="Drafts, waiting for approval and inactive">Unpublished</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in data.companies" :key="row.id" :class="row.id == ownCompany ? 'us-own' : ''">
                                <td class="us-name">{{ row.name }}</td>
                                <td v-for="t in types" :key="t" class="us-num">{{ num(row.content.publishedByType[t] || 0) }}</td>
                                <td class="us-num">{{ num(row.content.folders) }}</td>
                                <td class="us-num">{{ num(row.content.drafts + row.content.awaitingApproval + row.content.inactive) }}</td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <td class="us-name">All companies</td>
                                <td v-for="t in types" :key="t" class="us-num">{{ num(data.totals.content.publishedByType[t] || 0) }}</td>
                                <td class="us-num">{{ num(data.totals.content.folders) }}</td>
                                <td class="us-num">{{ num(data.totals.content.drafts + data.totals.content.awaitingApproval + data.totals.content.inactive) }}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                <ul class="us-notes">
                    <li>Admin logins have no last sign-in time, so "Editors" counts the different people who changed content in the last 30 days.</li>
                    <li>People counts (team logins, editors, AI users) are per company. Someone in two companies counts in both.</li>
                    <li>AI cost is IronBook's own estimate from tokens at list prices. It is for IronBook only; customers never see it.</li>
                </ul>
            </template>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';

// IronBook operator view of usage across every company (routes/usage.ts). Same audience as the
// competitive report: SuperAdmins of the report company; everyone else gets 403.
type Col = { key: string, label: string, title: string, get: (r: any) => any, money?: boolean };
const COLUMNS: Col[] = [
    { key: 'name', label: 'Company', title: 'Company name and ID', get: r => r.name },
    { key: 'team', label: 'Team logins', title: 'Team (phone) logins, including inactive', get: r => r.teamLogins.total },
    { key: 'active7', label: 'Active 7 d', title: 'Team logins that opened IronBook in the last 7 days', get: r => r.teamLogins.active7 },
    { key: 'active30', label: 'Active 30 d', title: 'Team logins that opened IronBook in the last 30 days', get: r => r.teamLogins.active30 },
    { key: 'never', label: 'Never signed in', title: 'Team logins (not deactivated) that have never signed in', get: r => r.teamLogins.never },
    { key: 'admins', label: 'Admin logins', title: 'Admin (email) logins with access to this company', get: r => r.adminLogins.total },
    { key: 'editors', label: 'Editors 30 d', title: 'Different people who changed content in the last 30 days', get: r => r.last30.editors },
    { key: 'employees', label: 'Employees', title: 'Employee records that are not terminated', get: r => r.employees.current },
    { key: 'published', label: 'Published', title: 'Published processes, Powerlists, sheets, videos and links (not folders)', get: r => r.content.published },
    { key: 'drafts', label: 'Drafts', title: 'Drafts and items waiting for approval', get: r => r.content.drafts + r.content.awaitingApproval },
    { key: 'signoffs', label: 'Sign-offs 30 d', title: 'Sign-offs in the last 30 days', get: r => r.last30.signOffs },
    { key: 'checklists', label: 'Powerlist runs 30 d', title: 'Powerlist / checklist runs logged in the last 30 days', get: r => r.last30.checklistRuns },
    { key: 'meetings', label: 'Meetings 30 d', title: 'Meetings run in the last 30 days', get: r => r.last30.meetingsRun },
    { key: 'recordings', label: 'Recordings 30 d', title: 'Recordings turned into drafts or meeting notes in the last 30 days', get: r => r.last30.recordingsProcessed },
    { key: 'aiCalls', label: 'AI calls 30 d', title: 'AI requests in the last 30 days', get: r => r.last30.aiCalls },
    { key: 'aiCost', label: 'AI cost 30 d', title: 'Estimated Anthropic cost, last 30 days (IronBook only)', get: r => r.last30.aiCostUSD, money: true }
];

export default defineComponent({
    name: 'UsageStats',
    data() {
        return {
            data: null as any,
            denied: false,
            error: '',
            sortKey: 'active30',
            sortDir: -1,
            columns: COLUMNS
        }
    },
    computed: {
        ownCompany(): number { return Number(this.$store.state.user?.companyID) || 0; },
        generated(): string {
            const d = this.data?.generatedAt ? new Date(this.data.generatedAt) : null;
            return d && !isNaN(d.getTime()) ? d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '';
        },
        sorted(): any[] {
            const col = COLUMNS.find(c => c.key == this.sortKey) || COLUMNS[0];
            const rows = [ ...(this.data?.companies || []) ];
            return rows.sort((a, b) => {
                const x = col.get(a), y = col.get(b);
                const cmp = typeof x == 'string' ? String(x).localeCompare(String(y)) : (Number(x) || 0) - (Number(y) || 0);
                return cmp * this.sortDir || a.id - b.id;
            });
        },
        types(): string[] {
            const order = [ 'PROCESS', 'CHECKLIST', 'SHEET', 'TIMELINE', 'VIDEO', 'LINK' ];
            const present = Object.keys(this.data?.totals?.content?.publishedByType || {});
            return [ ...order.filter(t => present.includes(t)), ...present.filter(t => !order.includes(t)).sort() ];
        },
        strip(): any[] {
            const t = this.data?.totals;
            if(!t) {
                return [];
            }
            const companies = (this.data.companies || []).filter((c: any) => c.teamLogins.active30 > 0 || c.last30.editors > 0).length;
            return [
                { label: 'Team logins active (30 d)', value: this.num(t.teamLogins.active30), sub: `${this.num(t.teamLogins.active7)} in the last 7 days · ${this.num(t.teamLogins.total)} total` },
                { label: 'Companies with activity', value: this.num(companies), sub: `of ${this.num((this.data.companies || []).length)}` },
                { label: 'Sign-offs', value: this.num(t.last30.signOffs), sub: `${this.num(t.last30.checklistRuns)} Powerlist runs` },
                { label: 'Meetings run', value: this.num(t.last30.meetingsRun), sub: `${this.num(t.last30.recordingsProcessed)} recordings processed` },
                { label: 'AI calls', value: this.num(t.last30.aiCalls), sub: `about ${this.money(t.last30.aiCostUSD)}` }
            ];
        }
    },
    async created() {
        await this.load();
        this.$store.state.routeLoaded = true;
    },
    methods: {
        async load() {
            this.error = '';
            try {
                this.data = (await axios.get(`${this.$store.state.apiUrl}/api/usage/overview`)).data;
            } catch(e: any) {
                if(e?.response?.status == 403) {
                    this.denied = true;
                } else {
                    this.error = 'IronBook usage could not be loaded.';
                }
            }
        },
        sortBy(key: string) {
            if(this.sortKey == key) {
                this.sortDir = -this.sortDir;
            } else {
                this.sortKey = key;
                this.sortDir = key == 'name' ? 1 : -1;
            }
        },
        num(v: any): string { return (Number(v) || 0).toLocaleString(); },
        money(v: any): string { return '$' + (Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
        fmt(c: Col, v: any): string { return c.money ? this.money(v) : this.num(v); },
        typeLabel(t: string): string { return (this.$store.state.playbookNodeTypeMap || {})[t] || t.charAt(0) + t.slice(1).toLowerCase(); }
    }
});
</script>

<style scoped>
.us { max-width: 1200px; margin: 0 auto; padding: 20px 14px 70px; color: #2a2a2a; line-height: 1.55; }
.us-back { display: inline-block; font-size: 13px; color: #a0958a; text-decoration: none; margin-bottom: 14px; }
.us-empty { background: #fff; border: 1px solid #f6e3d3; border-radius: 14px; padding: 22px; color: #66625e; }
.us-retry { margin-left: 8px; background: #fff; border: 1px solid #e3dcd5; border-radius: 999px; padding: 4px 12px; font: inherit; font-size: 13px; cursor: pointer; }
.us-kicker { font-family: ui-monospace, Menlo, monospace; font-size: 12px; letter-spacing: .06em; text-transform: uppercase; color: #a0958a; overflow-wrap: anywhere; }
.us-title { margin: 6px 0 4px; font-size: 28px; line-height: 1.2; }
.us-lede { font-size: 15px; color: #66625e; max-width: 70ch; margin: 0 0 18px; }
.us-strip { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 12px; margin: 0 0 22px; }
.us-stat { background: #fffaf5; border: 1px solid #e8cbb4; border-left: 4px solid #ef7d26; border-radius: 12px; padding: 14px 16px; min-width: 0; }
.us-stat-n { font-size: 26px; font-weight: 700; line-height: 1.1; font-variant-numeric: tabular-nums; }
.us-stat-l { color: #985018; font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; margin-top: 4px; }
.us-stat-s { color: #6c625b; font-size: 12.5px; margin-top: 2px; }
.us-warn { background: #fff1e6; border-radius: 8px; padding: 10px 14px; margin: 0 0 16px; font-size: 13.5px; }
.us-h { font-size: 18px; margin: 26px 0 2px; }
.us-hint { font-size: 12.5px; color: #8b8682; margin: 0; }
.us-tablewrap { overflow-x: auto; margin: 10px 0; border: 1px solid #ece6e0; border-radius: 8px; background: #fff; max-width: 100%; }
.us-table { border-collapse: collapse; width: 100%; font-size: 13.5px; }
.us-table th, .us-table td { padding: 8px 10px; border-bottom: 1px solid #f0ebe6; text-align: left; vertical-align: top; }
.us-table thead th { font-family: inherit; font-weight: 600; font-size: 12.5px; line-height: 1.3; color: #6c625b; vertical-align: bottom; }
.us-table .us-num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
.us-table .us-name { position: sticky; left: 0; background: #fff; font-weight: 600; min-width: 140px; max-width: 220px; z-index: 1; }
.us-table thead .us-name { font-weight: 600; }
.us-sort { background: none; border: 0; padding: 0; font: inherit; color: inherit; cursor: pointer; text-align: inherit; white-space: normal; min-width: 54px; }
.us-sort:hover { color: #b84f00; }
.us-sort:focus-visible { outline: 2px solid #ef7d26; outline-offset: 2px; border-radius: 3px; }
.us-sorted .us-sort { color: #b84f00; }
.us-id { color: #a0958a; font-weight: 400; font-size: 12px; }
.us-table tr.us-own td, .us-table tr.us-own .us-name { background: #fffaf5; }
.us-table tfoot td, .us-table tfoot .us-name { font-weight: 700; border-top: 2px solid #e8cbb4; border-bottom: 0; background: #fbf8f5; }
.us-notes { font-size: 12.5px; color: #8b8682; padding-left: 18px; margin-top: 18px; max-width: 80ch; }
@media (max-width: 600px) {
    .us-title { font-size: 22px; }
    .us-strip { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
    .us-stat { padding: 12px; }
    .us-stat-n { font-size: 22px; }
    .us-table .us-name { min-width: 110px; max-width: 140px; }
}
</style>
